/**
 * Phase 126.B — Classroom Support Hub profile-page layout.
 *
 * Renders the canonical 5-part structure for every profile page
 * (ADHD, Giftedness, 2e) so they read as a set:
 *
 *   1. Looks like in your room   (observable behaviours)
 *   2. How it can affect learning (honest, never deficit-only)
 *   3. Strengths it brings        (reframe)
 *   4. What they thrive on
 *   5. Strategies that help       (CTA to generator)
 *
 * The page is fully public — no auth, no AppShell. Top nav links back
 * to the Hub index + the main site. Footer cross-links to UDL Profile
 * + Individual Student Plan.
 *
 * Voice: trusted colleague, teacher-to-teacher, strengths-first. The
 * spec calls these out as non-negotiables — see
 * /app/customer-assets/.../emergent_prompt_classroom_support_hub.md.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Eye, Heart, Lightbulb, Sparkles, Wrench } from "lucide-react";
import useDocumentMeta from "../../lib/useDocumentMeta";
import { markGuideRead } from "../../lib/hubReadGuides";
import AuthAwareSupportLayout from "./AuthAwareSupportLayout";

const SECTION_ICONS = {
  looks_like: Eye,
  affects: Wrench,
  strengths: Sparkles,
  thrives: Heart,
  strategies: Lightbulb,
};

const SECTION_TITLES = {
  looks_like: "What it can look like in your room",
  affects: "How it can affect learning",
  strengths: "The strengths it brings",
  thrives: "What these students thrive on",
  strategies: "Strategies that help",
};

function Section({ id, body, bullets, footer }) {
  const Icon = SECTION_ICONS[id];
  return (
    <section
      className="border-t border-[#4a6984]/10 pt-10 mt-10 first:border-t-0 first:pt-0 first:mt-0"
      data-testid={`hub-section-${id}`}
    >
      <div className="flex items-center gap-3 mb-5">
        <span className="h-10 w-10 rounded-lg bg-[#96ded1]/20 grid place-items-center">
          <Icon className="h-5 w-5 text-[#1c2d3d]" strokeWidth={2} />
        </span>
        <h2 className="font-extrabold text-2xl text-[#1c2d3d] tracking-tight">
          {SECTION_TITLES[id]}
        </h2>
      </div>
      {body ? (
        <p className="text-[#4a6984] text-lg leading-relaxed mb-4">{body}</p>
      ) : null}
      {bullets?.length ? (
        <ul className="space-y-2.5">
          {bullets.map((b, i) => (
            <li
              key={i}
              className="flex items-start text-[#4a6984] leading-relaxed"
              data-testid={`hub-section-${id}-bullet-${i}`}
            >
              <span className="ae-marker mt-2.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {footer ? <div className="mt-5">{footer}</div> : null}
    </section>
  );
}

export default function ProfilePageLayout({
  slug,
  metaTitle,
  metaDescription,
  hero,
  sections,
  generators,
  readNext,
  adultNote,
}) {
  useDocumentMeta({
    title: metaTitle,
    description: metaDescription,
    type: "article",
  });

  useEffect(() => {
    markGuideRead(slug);
  }, [slug]);

  return (
    <AuthAwareSupportLayout title="Classroom Support">
      {({ inShell }) => (
        <div
          className="min-h-screen bg-[#f9fafb] text-[#1c2d3d]"
          data-testid={`classroom-support-page-${slug}`}
        >
          {/* Top bar — only when NOT signed in. Signed-in users see the
              AppShell left rail + InPageTopBar instead. The
              "Classroom Support Hub" link is preserved inline in the
              hero for both states (so signed-in users can still hop
              between profile pages without going to the left rail). */}
          {!inShell ? (
            <header className="border-b border-[#4a6984]/10 bg-white">
              <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
                <Link
                  to="/classroom-support"
                  className="inline-flex items-center text-sm font-bold text-[#4a6984] hover:text-[#1c2d3d]"
                  data-testid="hub-back"
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Classroom Support Hub
                </Link>
                <Link
                  to="/"
                  className="text-sm font-bold text-[#4a6984] hover:text-[#1c2d3d]"
                  data-testid="hub-home"
                >
                  Atomic Edu
                </Link>
              </div>
            </header>
          ) : null}

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-5 pt-12 pb-8">
        <p className="text-xs uppercase tracking-[0.28em] font-extrabold text-[#4a6984]/70 mb-3">
          {inShell ? (
            <Link
              to="/classroom-support"
              className="hover:text-[#1c2d3d] transition"
              data-testid="hub-back-eyebrow"
            >
              ← Classroom Support Hub
            </Link>
          ) : (
            "Classroom Support Hub"
          )}
        </p>
        <h1
          className="font-black text-4xl sm:text-5xl lg:text-6xl text-[#1c2d3d] tracking-tight leading-tight"
          data-testid="hub-title"
        >
          {hero.title}
        </h1>
        {hero.tagline ? (
          <p className="mt-5 text-lg text-[#4a6984] leading-relaxed max-w-2xl">
            {hero.tagline}
          </p>
        ) : null}
      </section>

      {/* Body */}
      <main className="max-w-3xl mx-auto px-5 pb-12">
        <article className="ae-card p-7 sm:p-10">
          {sections.map((s) => (
            <Section key={s.id} {...s} />
          ))}
        </article>

        {/* Generate a plan — bold mint card for the current profile's
            generator(s). For ADHD/Giftedness this is a single card; for
            2e it's two cards because the spec links to both generators. */}
        {generators?.length ? (
          <section
            className="mt-10"
            data-testid="hub-generate-section"
          >
            <p className="text-[11px] uppercase tracking-[0.28em] font-black text-[#1c2d3d] mb-3">
              Free generator
            </p>
            <div
              className={
                generators.length > 1
                  ? "grid sm:grid-cols-2 gap-4"
                  : "grid grid-cols-1 gap-4"
              }
            >
              {generators.map((cta, i) => (
                <Link
                  key={i}
                  to={cta.to}
                  data-testid={`hub-generate-${i}`}
                  className="block rounded-xl p-6 bg-[#96ded1] hover:bg-[#7fd0c1] transition group shadow-sm"
                >
                  <h3 className="font-extrabold text-xl text-[#1c2d3d] tracking-tight">
                    {cta.title}
                  </h3>
                  <p className="text-sm text-[#1c2d3d]/80 mt-2 leading-relaxed">
                    {cta.subtitle}
                  </p>
                  <span className="inline-flex items-center text-sm font-extrabold text-[#1c2d3d] mt-4 group-hover:translate-x-0.5 transition">
                    {cta.cta || "Generate a plan"} <ArrowRight className="h-4 w-4 ml-1.5" />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {/* Read next — mint-tinted cards pointing at the OTHER two
            profile pages. Bolder eyebrow than before so the section
            is impossible to miss. */}
        {readNext?.length ? (
          <section
            className="mt-10"
            data-testid="hub-read-next-section"
          >
            <p className="text-[11px] uppercase tracking-[0.28em] font-black text-[#1c2d3d] mb-3">
              Read next
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {readNext.map((cta, i) => (
                <Link
                  key={i}
                  to={cta.to}
                  data-testid={`hub-read-next-${i}`}
                  className="block rounded-xl p-6 bg-[#96ded1]/25 hover:bg-[#96ded1]/45 border border-[#96ded1] transition group"
                >
                  <h3 className="font-extrabold text-lg text-[#1c2d3d] tracking-tight">
                    {cta.title}
                  </h3>
                  <p className="text-sm text-[#1c2d3d]/80 mt-1.5 leading-relaxed">
                    {cta.subtitle}
                  </p>
                  <span className="inline-flex items-center text-sm font-extrabold text-[#1c2d3d] mt-3 group-hover:translate-x-0.5 transition">
                    {cta.cta || "Read the guide"} <ArrowRight className="h-4 w-4 ml-1.5" />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {/* Adult-recognize-yourself note (2e only — optional per the spec) */}
        {adultNote ? (
          <div
            className="mt-8 ae-card p-6 bg-[#96ded1]/10 border-[#96ded1]/40"
            data-testid="hub-adult-note"
          >
            <p className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#1c2d3d]/70 mb-2">
              A quiet aside
            </p>
            <p className="text-[#1c2d3d] leading-relaxed">{adultNote}</p>
          </div>
        ) : null}

        {/* Cross-links to related Atomic Edu features */}
        <nav
          className="mt-10 pt-8 border-t border-[#4a6984]/10 text-sm text-[#4a6984]"
          data-testid="hub-crosslinks"
        >
          <p className="font-bold uppercase tracking-[0.2em] text-xs text-[#4a6984]/60 mb-3">
            Related in Atomic Edu
          </p>
          <ul className="space-y-2">
            <li>
              <Link
                to="/individual-student-plan"
                className="hover:text-[#1c2d3d] underline underline-offset-4"
                data-testid="hub-link-isp"
              >
                Individual Student Plan — generate a term-long pathway for one student
              </Link>
            </li>
            <li>
              <Link
                to="/skills"
                className="hover:text-[#1c2d3d] underline underline-offset-4"
                data-testid="hub-link-skills"
              >
                Transferable Skills Library — whole-class lens on the seven skills
              </Link>
            </li>
            <li>
              <Link
                to="/"
                className="hover:text-[#1c2d3d] underline underline-offset-4"
                data-testid="hub-link-home"
              >
                See everything Atomic Edu generates for Ontario teachers
              </Link>
            </li>
          </ul>
        </nav>
      </main>
        </div>
      )}
    </AuthAwareSupportLayout>
  );
}
